// Admin → Play Store: every Google Play review with a reply box (posted to Google Play), and how the
// app is rated: overall, over time, and by country, device, app version, Android version and
// language. The API copies reviews from Google every hour and Google's rating reports every 3 hours
// (apps/api/src/lib/play.ts). Review text is what reviewers wrote: shown as text, nothing more.
// "Suggest a reply" asks Claude for a draft in the reviewer's language (lib/play-suggest.ts), which
// fills the reply box for staff to edit and post.
import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router';
import { useInfiniteQuery, useQuery, useQueryClient } from '@tanstack/react-query';
import { Languages, RefreshCw, Sparkles, Star, TriangleAlert } from 'lucide-react';
import { api } from '../lib/api';
import { useCan } from '../lib/staff';
import { Stat } from './Overview';
import {
  Card,
  EmptyState,
  ErrorNote,
  fmtNum,
  PageHeader,
  run,
  SearchInput,
  SkeletonRows,
  Tabs,
  TimeAgo,
  useDebounced,
} from '../components/ui';

interface SyncRow {
  ok: boolean;
  rows: number | null;
  error: string | null;
  at: number;
}
interface Breakdown {
  value: string;
  total: number | null;
  recent: number | null;
  days: number;
  lastDay: string;
}
interface Overview {
  configured: boolean;
  bucket: string | null;
  status: {
    reviews: SyncRow | null;
    reports: SyncRow | null;
    files: number;
    lastImport: number | null;
    failedFiles: { name: string; error: string | null; at: number }[];
  };
  rating: { total: number; asOf: string } | null;
  trend: { day: string; daily: number | null; total: number | null }[];
  stars30: { stars: number; n: number }[];
  stars90: { stars: number; n: number }[];
  needsReply: number;
  versions: { value: string; code: number | null; n: number; avg: number }[];
  breakdowns: Record<Dimension, Breakdown[]>;
  replyMax: number;
}
interface Review {
  id: string;
  author: string | null;
  stars: number;
  text: string | null;
  english: string | null;
  language: string | null;
  device: string | null;
  deviceName: string | null;
  androidVersion: number | null;
  appVersionCode: number | null;
  appVersionName: string | null;
  thumbsUp: number | null;
  writtenAt: number;
  replyText: string | null;
  repliedAt: number | null;
  repliedBy: string | null;
}
type Dimension = 'country' | 'device' | 'app_version' | 'os_version' | 'language';

const DIMENSIONS: { key: Dimension; label: string; head: string }[] = [
  { key: 'country', label: 'Country', head: 'Country or region' },
  { key: 'device', label: 'Device', head: 'Device' },
  { key: 'app_version', label: 'App version', head: 'App version' },
  { key: 'os_version', label: 'Android version', head: 'Android version' },
  { key: 'language', label: 'Language', head: 'Language' },
];

const regionNames = (() => {
  try {
    return new Intl.DisplayNames(['en'], { type: 'region' });
  } catch {
    return null;
  }
})();
const languageNames = (() => {
  try {
    return new Intl.DisplayNames(['en'], { type: 'language' });
  } catch {
    return null;
  }
})();
function languageName(tag: string | null): string {
  if (!tag) return 'Unknown language';
  try {
    return languageNames?.of(tag.replace('_', '-')) ?? tag;
  } catch {
    return tag;
  }
}
function valueLabel(d: Dimension, v: string): string {
  if (d === 'country' && /^[A-Z]{2}$/.test(v)) {
    try {
      return `${regionNames?.of(v) ?? v} (${v})`;
    } catch {
      return v;
    }
  }
  if (d === 'language') return languageName(v);
  return v;
}
const stars1 = (n: number | null) => (n === null ? '—' : n.toFixed(2));
const isEnglish = (lang: string | null) => !lang || lang.toLowerCase().startsWith('en');

function Stars({ n, size = 14 }: { n: number; size?: number }) {
  return (
    <span className="inline-flex items-center gap-px" role="img" aria-label={`${n} of 5 stars`}>
      {[1, 2, 3, 4, 5].map((i) => (
        <Star
          key={i}
          size={size}
          strokeWidth={1.75}
          aria-hidden="true"
          className={i <= n ? 'fill-ink text-ink' : 'text-ink/25'}
        />
      ))}
    </span>
  );
}

export function PlayStore() {
  const [params, setParams] = useSearchParams();
  const tab = (params.get('tab') ?? 'reviews') as 'reviews' | 'ratings';
  const qc = useQueryClient();
  const canReply = useCan()('play.reply');
  const { data, error, refetch } = useQuery({
    queryKey: ['play-overview'],
    queryFn: () => api<Overview>('/play/overview'),
    refetchInterval: 60_000,
  });
  async function sync() {
    const r = await run(
      () => api('/play/sync', { body: {} }),
      'Asked Google for the latest. It takes a minute.',
    );
    if (r)
      window.setTimeout(() => {
        void qc.invalidateQueries({ queryKey: ['play-overview'] });
        void qc.invalidateQueries({ queryKey: ['play-reviews'] });
      }, 15_000);
  }
  return (
    <div className="space-y-5">
      <PageHeader
        title="Play Store"
        description="Every Google Play review, with replies posted straight to Google Play, and how the app is rated by country, device and version. Reviews are copied from Google every hour, ratings from Google’s daily reports."
        actions={
          canReply && (
            <button type="button" className="btn-ghost h-10" onClick={() => void sync()}>
              <RefreshCw size={15} aria-hidden="true" />
              Sync now
            </button>
          )
        }
      />
      {error && !data && <ErrorNote error={error} onRetry={() => void refetch()} />}
      {data && <SyncStatus o={data} />}
      <Tabs
        label="Section"
        value={tab}
        onChange={(k) => setParams((p) => (p.set('tab', k), p), { replace: true })}
        items={[
          { key: 'reviews', label: 'Reviews', count: data?.needsReply, hot: true },
          { key: 'ratings', label: 'Ratings' },
        ]}
      />
      {!data && !error ? (
        <SkeletonRows rows={4} />
      ) : tab === 'reviews' ? (
        <Reviews initial={data?.needsReply === 0 ? 'all' : 'reply'} />
      ) : data ? (
        <Ratings o={data} />
      ) : null}
    </div>
  );
}

/** One line per source: when it last worked, or what Google said. */
function SyncStatus({ o }: { o: Overview }) {
  if (!o.configured)
    return (
      <p className="card flex items-start gap-2 p-4 text-[14px] text-graphite">
        <TriangleAlert size={16} className="mt-0.5 shrink-0 text-berry-deep" aria-hidden="true" />
        FIREBASE_SERVICE_ACCOUNT isn’t set, so nothing is copied from Google Play.
      </p>
    );
  // Google's refusal in one line; its own words stay one click away.
  const line = (label: string, r: SyncRow | null, ok: string, waiting: string) => {
    const refused = !!r && !r.ok && /\(403\)/.test(r.error ?? '');
    return (
      <div className="flex min-w-0 items-start gap-2">
        <span
          aria-hidden="true"
          className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${!r ? 'bg-ink/25' : r.ok ? 'bg-mint' : refused ? 'bg-sun' : 'bg-berry'}`}
        />
        <div className="min-w-0 text-[13px] leading-snug">
          <span className="font-semibold text-ink">{label}</span>{' '}
          {!r ? (
            <span className="text-stone">not copied yet (first run within the hour)</span>
          ) : r.ok ? (
            <span className="text-graphite">
              {ok} · <TimeAgo t={r.at} />
            </span>
          ) : (
            <>
              <span className={refused ? 'text-graphite' : 'text-berry-deep'}>
                {refused ? waiting : (r.error ?? 'failed').split('. ')[0]} · tried{' '}
                <TimeAgo t={r.at} />
              </span>
              <details className="mt-1">
                <summary className="cursor-pointer text-[12.5px] font-semibold text-stone">
                  Google’s message
                </summary>
                <p className="mt-1 break-words text-[12.5px] text-stone">{r.error}</p>
              </details>
            </>
          )}
        </div>
      </div>
    );
  };
  return (
    <div className="grid gap-2 md:grid-cols-2">
      {line(
        'Reviews',
        o.status.reviews,
        `${fmtNum(o.status.reviews?.rows ?? 0)} in the last 7 days`,
        'Google refused: check the account’s “Reply to reviews” permission in Play Console',
      )}
      {line(
        'Rating reports',
        o.status.reports,
        `${fmtNum(o.status.files)} files imported${o.status.failedFiles.length ? `, ${o.status.failedFiles.length} failed` : ''}`,
        'waiting for Google to open the report files (up to a day after adding the account in Play Console; checked every 3 hours)',
      )}
    </div>
  );
}

// ---------------------------------------------------------------- reviews

type Show = 'reply' | 'unanswered' | 'replied' | 'all';

/** `initial`: "Need a reply" when something does, else every review. */
function Reviews({ initial }: { initial: Show }) {
  const [show, setShow] = useState<Show>(initial);
  const [stars, setStars] = useState('');
  const [q, setQ] = useState('');
  const search = useDebounced(q.trim());
  const qs = new URLSearchParams({ show });
  if (stars) qs.set('stars', stars);
  if (search) qs.set('q', search);
  const { data, error, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage, refetch } =
    useInfiniteQuery({
      queryKey: ['play-reviews', show, stars, search],
      queryFn: ({ pageParam }) =>
        api<{ reviews: Review[]; next: string | null; canTranslate: boolean; replyMax: number }>(
          `/play/reviews?${qs}${pageParam ? `&before=${encodeURIComponent(pageParam)}` : ''}`,
        ),
      initialPageParam: '',
      getNextPageParam: (p) => p.next ?? undefined,
      placeholderData: (p) => p,
    });
  const reviews = data?.pages.flatMap((p) => p.reviews) ?? [];
  const first = data?.pages[0];
  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 md:flex-row md:flex-wrap md:items-center">
        <Tabs
          label="Which reviews"
          value={show}
          onChange={setShow}
          items={[
            { key: 'reply', label: 'Need a reply' },
            { key: 'unanswered', label: 'No reply' },
            { key: 'replied', label: 'Replied' },
            { key: 'all', label: 'All' },
          ]}
        />
        <div className="grid gap-2 sm:grid-cols-[140px_minmax(0,1fr)] md:ml-auto md:flex md:items-center">
          <select
            aria-label="Stars"
            className="input h-10 py-0 text-[14px] md:w-36"
            value={stars}
            onChange={(e) => setStars(e.target.value)}
          >
            <option value="">Any stars</option>
            {[5, 4, 3, 2, 1].map((s) => (
              <option key={s} value={s}>
                {s} star{s === 1 ? '' : 's'}
              </option>
            ))}
          </select>
          <SearchInput value={q} onChange={setQ} placeholder="Search words, name or device" />
        </div>
      </div>
      {show === 'reply' && (
        <p className="text-[13px] text-stone">
          1 to 3 stars, with words, from the last 30 days, and no reply yet. A thoughtful reply
          often gets the rating raised, and other people read it too.
        </p>
      )}
      {error && !data ? (
        <ErrorNote error={error} onRetry={() => void refetch()} />
      ) : isLoading ? (
        <SkeletonRows rows={5} />
      ) : reviews.length === 0 ? (
        <EmptyState
          icon={Star}
          title={show === 'reply' ? 'Nothing waiting for a reply' : 'No reviews here'}
        >
          {show === 'reply'
            ? 'Every recent low rating with words has an answer.'
            : 'Google’s reviews API covers the last 7 days; older ones arrive with the monthly reports.'}
        </EmptyState>
      ) : (
        <ul className="space-y-3">
          {reviews.map((r) => (
            <ReviewCard
              key={r.id}
              r={r}
              canTranslate={!!first?.canTranslate}
              max={first?.replyMax ?? 350}
            />
          ))}
        </ul>
      )}
      {hasNextPage && (
        <button
          type="button"
          className="btn-ghost w-full"
          disabled={isFetchingNextPage}
          onClick={() => void fetchNextPage()}
        >
          {isFetchingNextPage ? 'Loading…' : 'Show older reviews'}
        </button>
      )}
    </div>
  );
}

function ReviewCard({ r, canTranslate, max }: { r: Review; canTranslate: boolean; max: number }) {
  const [original, setOriginal] = useState(false);
  /** The reply box, opened empty (or with the posted reply), or opened with Claude's draft. */
  const [open, setOpen] = useState<false | 'write' | 'suggest'>(false);
  const can = useCan();
  // Claude needs the API key (canTranslate says it's set) and the staff member must be able to reply.
  const canSuggest = canTranslate && can('play.reply');
  const canReply = can('play.reply');
  const shown = original || !r.english ? r.text : r.english;
  const facts = [
    r.deviceName ?? r.device,
    r.appVersionName
      ? `v${r.appVersionName}${r.appVersionCode ? ` (${r.appVersionCode})` : ''}`
      : r.appVersionCode
        ? `build ${r.appVersionCode}`
        : null,
    r.androidVersion ? `Android API ${r.androidVersion}` : null,
    languageName(r.language),
  ].filter(Boolean);
  return (
    <li className="card p-4 sm:p-5">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <Stars n={r.stars} />
        <span className="font-semibold">{r.author ?? 'A Google user'}</span>
        <span className="ml-auto text-[13px] text-stone">
          <TimeAgo t={r.writtenAt} />
        </span>
      </div>
      <p className="mt-1 text-[12.5px] text-stone">{facts.join(' · ')}</p>
      {shown ? (
        <p className="mt-3 whitespace-pre-wrap break-words text-[15px] leading-relaxed text-ink">
          {shown}
        </p>
      ) : (
        <p className="mt-3 text-[14px] italic text-stone">Stars only, no words.</p>
      )}
      {r.english && (
        <button
          type="button"
          className="mt-1.5 inline-flex items-center gap-1 text-[12.5px] font-semibold text-graphite underline decoration-ink/25 underline-offset-2"
          onClick={() => setOriginal((v) => !v)}
        >
          <Languages size={13} aria-hidden="true" />
          {original ? 'Show Google’s English' : `Show the original (${languageName(r.language)})`}
        </button>
      )}
      {r.replyText && !open && (
        <div className="mt-4 rounded-[12px] border-l-[3px] border-ink bg-ink/[0.035] px-4 py-3">
          <div className="text-[12.5px] font-semibold text-stone">
            Your reply · {r.repliedBy ? 'from Admin' : 'from Play Console'}
            {r.repliedAt && (
              <>
                {' · '}
                <TimeAgo t={r.repliedAt} />
              </>
            )}
          </div>
          <p className="mt-1 whitespace-pre-wrap break-words text-[14px] leading-relaxed">
            {r.replyText}
          </p>
        </div>
      )}
      {open ? (
        <Composer
          r={r}
          canTranslate={canTranslate}
          canSuggest={canSuggest}
          suggestFirst={open === 'suggest'}
          max={max}
          onDone={() => setOpen(false)}
        />
      ) : !canReply ? null : (
        <div className="mt-4 flex flex-wrap gap-2">
          <button
            type="button"
            className={`${r.replyText ? 'btn-ghost' : 'btn-primary'} h-9 px-4 text-[14px]`}
            onClick={() => setOpen('write')}
          >
            {r.replyText ? 'Edit reply' : 'Reply'}
          </button>
          {canSuggest && !r.replyText && (
            <button
              type="button"
              className="btn-ghost h-9 px-3.5 text-[14px]"
              onClick={() => setOpen('suggest')}
            >
              <Sparkles size={14} aria-hidden="true" /> Suggest a reply
            </button>
          )}
        </div>
      )}
    </li>
  );
}

/**
 * The reply box. Written in English; for a review in another language, "Translate" shows the
 * reply in that language with a literal translation back, and staff choose which one to post.
 * "Suggest a reply" fills it with Claude's draft, already in the reviewer's language, with what it
 * says in English underneath. Google shows the reply publicly under the review and refuses more
 * than about 350 characters.
 */
function Composer({
  r,
  canTranslate,
  canSuggest,
  suggestFirst,
  max,
  onDone,
}: {
  r: Review;
  canTranslate: boolean;
  canSuggest: boolean;
  /** Opened from "Suggest a reply": ask for a draft straight away. */
  suggestFirst: boolean;
  max: number;
  onDone: () => void;
}) {
  const qc = useQueryClient();
  const [text, setText] = useState(r.replyText ?? '');
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState<{
    for: string;
    translation: string;
    backToEnglish: string;
  } | null>(null);
  /** Claude's last draft: in the reply box until staff empty it. */
  const [draft, setDraft] = useState<{
    reply: string;
    english: string;
    language: string;
  } | null>(null);
  const [suggesting, setSuggesting] = useState(false);
  const ref = useRef<HTMLTextAreaElement>(null);
  useEffect(() => ref.current?.focus(), []);
  const asked = useRef(false);
  useEffect(() => {
    if (suggestFirst && canSuggest && !asked.current) {
      asked.current = true;
      void suggest();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  // The box holds Claude's draft (possibly edited), in the reviewer's own language.
  const theirLanguage = !!draft && !!text.trim() && !isEnglish(draft.language);
  const foreign = !isEnglish(r.language) && !theirLanguage;
  const fresh = preview && preview.for === text.trim() ? preview : null;
  async function suggest() {
    setSuggesting(true);
    const s = await run(() =>
      api<{ reply: string; english: string; language: string }>(
        `/play/reviews/${encodeURIComponent(r.id)}/suggest`,
        { body: {} },
      ),
    );
    setSuggesting(false);
    if (!s) return;
    setDraft(s);
    setText(s.reply);
    setPreview(null);
    ref.current?.focus();
  }
  async function translate() {
    setBusy(true);
    const t = await run(() =>
      api<{ translation: string; backToEnglish: string }>(
        `/play/reviews/${encodeURIComponent(r.id)}/translate`,
        {
          body: { text: text.trim() },
        },
      ),
    );
    setBusy(false);
    if (t) setPreview({ for: text.trim(), ...t });
  }
  async function post(body: string) {
    setBusy(true);
    const ok = await run(
      () => api(`/play/reviews/${encodeURIComponent(r.id)}/reply`, { body: { text: body } }),
      'Reply posted on Google Play.',
    );
    setBusy(false);
    if (!ok) return;
    await Promise.all([
      qc.invalidateQueries({ queryKey: ['play-reviews'] }),
      qc.invalidateQueries({ queryKey: ['play-overview'] }),
      qc.invalidateQueries({ queryKey: ['overview'] }),
    ]);
    onDone();
  }
  const len = text.trim().length;
  return (
    <div className="mt-4 space-y-3">
      <label className="block">
        <span className="sr-only">Reply</span>
        <textarea
          ref={ref}
          rows={3}
          className="input min-h-[96px] resize-y text-[15px] leading-relaxed"
          placeholder={
            suggesting
              ? 'Claude is writing a draft…'
              : 'Thank them, or say what you’ll do about it. Everyone on Google Play can read this.'
          }
          aria-busy={suggesting}
          value={text}
          onChange={(e) => setText(e.target.value)}
        />
      </label>
      {draft && text.trim() && (
        <div className="rounded-[12px] bg-ink/[0.035] px-4 py-3 text-[13.5px] leading-relaxed">
          <div className="text-[12.5px] font-semibold text-stone">
            Claude’s draft{text.trim() !== draft.reply ? ', before your edits' : ''}
            {!isEnglish(draft.language) ? ', translated back to English separately' : ''}. Read it
            before posting.
          </div>
          {!isEnglish(draft.language) && (
            <p className="mt-1 whitespace-pre-wrap break-words text-graphite">{draft.english}</p>
          )}
        </div>
      )}
      <div className="flex flex-wrap items-center gap-2">
        <span
          className={`mr-auto text-[12.5px] tabular-nums ${len > max ? 'font-semibold text-berry-deep' : 'text-stone'}`}
        >
          {len}/{max}
        </span>
        <button type="button" className="btn-ghost h-9 px-3 text-[14px]" onClick={onDone}>
          Cancel
        </button>
        {canSuggest && (
          <button
            type="button"
            className="btn-ghost h-9 px-3 text-[14px]"
            disabled={busy || suggesting}
            onClick={() => void suggest()}
          >
            <Sparkles size={14} aria-hidden="true" />
            {suggesting ? 'Writing…' : draft ? 'Try another' : 'Suggest a reply'}
          </button>
        )}
        {foreign && canTranslate && (
          <button
            type="button"
            className="btn-ghost h-9 px-3 text-[14px]"
            disabled={busy || !len}
            onClick={() => void translate()}
          >
            <Languages size={14} aria-hidden="true" />
            Translate to {languageName(r.language)}
          </button>
        )}
        <button
          type="button"
          className={`${fresh ? 'btn-ghost' : 'btn-primary'} h-9 px-4 text-[14px]`}
          disabled={busy || !len || len > max}
          onClick={() => void post(text.trim())}
        >
          {foreign
            ? 'Post in English'
            : theirLanguage
              ? `Post in ${languageName(r.language)}`
              : 'Post reply'}
        </button>
      </div>
      {fresh && (
        <div className="rounded-[12px] border border-hairline bg-paper p-4">
          <div className="text-[12.5px] font-semibold text-stone">
            In {languageName(r.language)}
          </div>
          <p className="mt-1 whitespace-pre-wrap break-words text-[15px] leading-relaxed">
            {fresh.translation}
          </p>
          <div className="mt-3 text-[12.5px] font-semibold text-stone">What it says</div>
          <p className="mt-1 whitespace-pre-wrap text-[13.5px] leading-relaxed text-graphite">
            {fresh.backToEnglish}
          </p>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <span
              className={`mr-auto text-[12.5px] tabular-nums ${fresh.translation.length > max ? 'font-semibold text-berry-deep' : 'text-stone'}`}
            >
              {fresh.translation.length}/{max}
              {fresh.translation.length > max && ' · too long for Google: shorten the English'}
            </span>
            <button
              type="button"
              className="btn-primary h-9 px-4 text-[14px]"
              disabled={busy || fresh.translation.length > max}
              onClick={() => void post(fresh.translation)}
            >
              Post in {languageName(r.language)}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------- ratings

function Ratings({ o }: { o: Overview }) {
  const [dim, setDim] = useState<Dimension>('country');
  const reviews30 = o.stars30.reduce((a, s) => a + s.n, 0);
  const avg30 = reviews30 ? o.stars30.reduce((a, s) => a + s.stars * s.n, 0) / reviews30 : null;
  const reportsMissing = !o.trend.length;
  return (
    <div className="space-y-5">
      <div className="grid gap-3 sm:grid-cols-3">
        <Stat
          label="Rating on Google Play"
          value={o.rating ? `${o.rating.total.toFixed(2)} ★` : '—'}
          sub={
            o.rating
              ? `All ratings, as of ${o.rating.asOf}. The Store rounds it and weighs recent ones more.`
              : 'Arrives with Google’s first rating report.'
          }
        />
        <Stat
          label="Reviews, last 30 days"
          value={fmtNum(reviews30)}
          sub={avg30 !== null ? `Averaging ${avg30.toFixed(2)} ★` : 'None copied yet.'}
        />
        <Stat
          label="Need a reply"
          value={fmtNum(o.needsReply)}
          sub="1–3 stars with words, last 30 days"
          warn={o.needsReply > 0}
          to="/play?tab=reviews"
        />
      </div>

      <Card title="Average rating, last 180 days">
        {reportsMissing ? (
          <EmptyState icon={Star} title="Waiting for Google’s rating reports">
            {o.status.reports && !o.status.reports.ok
              ? o.status.reports.error
              : 'They’re read from Play Console’s report bucket every 3 hours. Access for a newly added account can take up to a day.'}
          </EmptyState>
        ) : (
          <TrendChart rows={o.trend} />
        )}
      </Card>

      <div className="grid grid-cols-1 items-start gap-5 lg:grid-cols-[minmax(0,1fr)_360px]">
        <Card title="By country, device and version" pad={false}>
          <div className="border-b border-hairline px-4 py-3 sm:px-5">
            <Tabs label="Break down by" value={dim} onChange={setDim} items={DIMENSIONS} />
          </div>
          <BreakdownTable d={dim} rows={o.breakdowns[dim] ?? []} />
        </Card>
        <div className="space-y-5">
          <Card title="Stars in reviews, last 90 days">
            <StarBars rows={o.stars90} />
          </Card>
          <Card title="By app version, from reviews" pad={false}>
            {o.versions.length ? (
              <table className="data-table table-compact w-full">
                <thead>
                  <tr>
                    <th>Version</th>
                    <th className="text-right">Reviews</th>
                    <th className="text-right">Average</th>
                  </tr>
                </thead>
                <tbody>
                  {o.versions.map((v) => (
                    <tr key={v.value}>
                      <td>
                        {v.value}
                        {v.code && v.value !== String(v.code) && (
                          <span className="ml-1 text-[12px] text-stone">({v.code})</span>
                        )}
                      </td>
                      <td className="text-right tabular-nums">{fmtNum(v.n)}</td>
                      <td className="text-right font-semibold tabular-nums">{v.avg.toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <p className="p-4 text-[14px] text-stone sm:p-5">
                No reviews in the last 90 days yet.
              </p>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
}

function BreakdownTable({ d, rows }: { d: Dimension; rows: Breakdown[] }) {
  const head = DIMENSIONS.find((x) => x.key === d)!.head;
  if (!rows.length)
    return (
      <p className="p-4 text-[14px] text-stone sm:p-5">
        Comes from Google’s rating reports, which haven’t been imported yet.
      </p>
    );
  return (
    <div className="overflow-x-auto">
      <table className="data-table table-compact w-full min-w-[520px]">
        <thead>
          <tr>
            <th>{head}</th>
            <th
              className="text-right"
              title="Every rating ever given, averaged (Google’s running total)."
            >
              All time
            </th>
            <th
              className="text-right"
              title="The mean of Google’s daily averages on days with new ratings."
            >
              Last 90 days
            </th>
            <th className="text-right">Days rated</th>
            <th className="text-right">Latest</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const drop = r.recent !== null && r.total !== null && r.recent < r.total - 0.3;
            return (
              <tr key={r.value}>
                <td className="max-w-[260px] truncate" title={r.value}>
                  {valueLabel(d, r.value)}
                </td>
                <td className="text-right font-semibold tabular-nums">{stars1(r.total)}</td>
                <td className="text-right tabular-nums">
                  <span className={drop ? 'font-semibold text-berry-deep' : ''}>
                    {stars1(r.recent)}
                  </span>
                  {drop && <span className="sr-only"> (lower than all time)</span>}
                </td>
                <td className="text-right tabular-nums text-graphite">{fmtNum(r.days)}</td>
                <td className="whitespace-nowrap text-right text-[13px] text-stone">{r.lastDay}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <p className="border-t border-hairline px-4 py-3 text-[12.5px] text-stone sm:px-5">
        Last 90 days in red: at least 0.3 below its all-time average. Google gives averages, not
        counts, so a value rated on few days moves a lot.
      </p>
    </div>
  );
}

function StarBars({ rows }: { rows: { stars: number; n: number }[] }) {
  const total = rows.reduce((a, r) => a + r.n, 0);
  const top = Math.max(1, ...rows.map((r) => r.n));
  if (!total) return <p className="text-[14px] text-stone">No reviews in the last 90 days yet.</p>;
  return (
    <ul className="space-y-2">
      {rows.map((r) => (
        <li
          key={r.stars}
          className="grid grid-cols-[44px_minmax(0,1fr)_64px] items-center gap-3 text-[13px]"
        >
          <span className="font-semibold tabular-nums">{r.stars} ★</span>
          <span className="h-2.5 rounded-full bg-ink/[0.06]">
            <span
              className="block h-full rounded-full bg-ink"
              style={{ width: `${(r.n / top) * 100}%` }}
            />
          </span>
          <span className="text-right tabular-nums text-graphite">
            {fmtNum(r.n)} <span className="text-stone">({Math.round((r.n / total) * 100)}%)</span>
          </span>
        </li>
      ))}
    </ul>
  );
}

/**
 * Google's all-time average as a line, and each day's new ratings as dots, on one star scale.
 * Hover (or tap) a day for its numbers; "Show the numbers" lists them as a table.
 */
function TrendChart({ rows }: { rows: Overview['trend'] }) {
  const box = useRef<HTMLDivElement>(null);
  const [w, setW] = useState(640);
  const [hover, setHover] = useState<number | null>(null);
  useEffect(() => {
    const el = box.current;
    if (!el) return;
    const ro = new ResizeObserver(([e]) => setW(Math.max(280, Math.round(e!.contentRect.width))));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const H = 220;
  const pad = { l: 36, r: 12, t: 12, b: 26 };
  const days = useMemo(() => rows.map((r) => Date.parse(`${r.day}T00:00:00Z`)), [rows]);
  const values = rows.flatMap((r) => [r.daily, r.total]).filter((v): v is number => v !== null);
  const lo = Math.min(4, Math.floor(Math.min(...values) * 2) / 2);
  const x0 = days[0]!;
  const x1 = Math.max(days.at(-1)!, x0 + 86_400_000);
  const x = (t: number) => pad.l + ((t - x0) / (x1 - x0)) * (w - pad.l - pad.r);
  const y = (v: number) => pad.t + ((5 - v) / (5 - lo)) * (H - pad.t - pad.b);
  const ticks: number[] = [];
  for (let v = lo; v <= 5.001; v += 0.5) ticks.push(v);
  const line = rows
    .map((r, i) => (r.total === null ? null : `${x(days[i]!)},${y(r.total)}`))
    .filter(Boolean)
    .join(' ');
  // A label at each month's first day shown; the partial first month only when there's room.
  const months = days
    .filter((t, i) => i > 0 && new Date(t).getUTCMonth() !== new Date(days[i - 1]!).getUTCMonth())
    .filter((t, i, all) => i > 0 || x(t) - pad.l > 0);
  if (days.length && (!months.length || x(months[0]!) - x(days[0]!) > 36)) months.unshift(days[0]!);
  const h = hover !== null ? rows[hover] : null;
  function onMove(clientX: number) {
    const rect = box.current!.getBoundingClientRect();
    const t = x0 + ((clientX - rect.left - pad.l) / (w - pad.l - pad.r)) * (x1 - x0);
    let best = 0;
    for (let i = 1; i < days.length; i++)
      if (Math.abs(days[i]! - t) < Math.abs(days[best]! - t)) best = i;
    setHover(best);
  }
  return (
    <div>
      <div className="mb-3 flex flex-wrap gap-x-5 gap-y-1 text-[12.5px] text-graphite">
        <span className="inline-flex items-center gap-2">
          <span aria-hidden="true" className="h-0.5 w-5 rounded-full bg-ink" />
          All-time average
        </span>
        <span className="inline-flex items-center gap-2">
          <span aria-hidden="true" className="h-2 w-2 rounded-full bg-sky-600" />
          That day’s new ratings
        </span>
      </div>
      <div
        ref={box}
        className="relative touch-pan-y select-none"
        onPointerMove={(e) => onMove(e.clientX)}
        onPointerDown={(e) => onMove(e.clientX)}
        onPointerLeave={() => setHover(null)}
      >
        <svg width={w} height={H} role="img" aria-label="Average rating over the last 180 days">
          {ticks.map((v) => (
            <g key={v}>
              <line
                x1={pad.l}
                x2={w - pad.r}
                y1={y(v)}
                y2={y(v)}
                stroke="currentColor"
                className="text-ink/[0.08]"
              />
              <text
                x={pad.l - 8}
                y={y(v) + 4}
                textAnchor="end"
                className="fill-stone text-[11px] tabular-nums"
              >
                {v.toFixed(1)}
              </text>
            </g>
          ))}
          {months.map((t) => (
            <text key={t} x={x(t)} y={H - 6} className="fill-stone text-[11px]">
              {new Date(t).toLocaleString('en', { month: 'short', timeZone: 'UTC' })}
            </text>
          ))}
          {h && (
            <line
              x1={x(days[hover!]!)}
              x2={x(days[hover!]!)}
              y1={pad.t}
              y2={H - pad.b}
              stroke="currentColor"
              className="text-ink/30"
            />
          )}
          <polyline
            points={line}
            fill="none"
            stroke="currentColor"
            strokeWidth={2}
            className="text-ink"
            strokeLinejoin="round"
          />
          {rows.map((r, i) =>
            r.daily === null ? null : (
              <circle
                key={r.day}
                cx={x(days[i]!)}
                cy={y(r.daily)}
                r={hover === i ? 5 : 4}
                className="fill-sky-600 stroke-paper"
                strokeWidth={2}
              />
            ),
          )}
        </svg>
        {h && (
          <div
            className="pointer-events-none absolute top-2 z-10 w-max rounded-[10px] bg-ink px-3 py-2 text-[12.5px] text-white shadow-none"
            style={{
              left: Math.min(Math.max(0, x(days[hover!]!) - 70), w - 150),
            }}
          >
            <div className="font-semibold">{h.day}</div>
            <div className="tabular-nums">All time: {stars1(h.total)}</div>
            <div className="tabular-nums">
              That day: {h.daily === null ? 'no new ratings' : h.daily.toFixed(2)}
            </div>
          </div>
        )}
      </div>
      <details className="mt-3 text-[13px]">
        <summary className="cursor-pointer font-semibold text-graphite">Show the numbers</summary>
        <div className="mt-2 max-h-64 overflow-y-auto">
          <table className="data-table table-compact w-full">
            <thead>
              <tr>
                <th>Day</th>
                <th className="text-right">All time</th>
                <th className="text-right">That day</th>
              </tr>
            </thead>
            <tbody>
              {[...rows].reverse().map((r) => (
                <tr key={r.day}>
                  <td className="tabular-nums">{r.day}</td>
                  <td className="text-right tabular-nums">{stars1(r.total)}</td>
                  <td className="text-right tabular-nums">{stars1(r.daily)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </details>
    </div>
  );
}
